<template>
  <PayRangePenetrationBar
    v-if="!readinessEnabled"
    variant="short"
    :range-penetration="rangePenetration"
    :currency-symbol="currencySymbol"
  />
  <span
    v-else
    class="fair-pay-status-short"
    :class="`fair-pay-status-short--${status}`"
  >
    <img
      v-if="showBelowIcon"
      class="fair-pay-status-short__icon fair-pay-status-short__icon--below"
      :src="arrowUpIcon"
      alt=""
      aria-hidden="true"
    />
    <img
      v-else-if="showAboveIcon"
      class="fair-pay-status-short__icon fair-pay-status-short__icon--above"
      :src="warningIcon"
      alt=""
      aria-hidden="true"
    />
    <span class="fair-pay-status-short__label">{{ label }}</span>
  </span>
</template>

<script setup>
import { computed } from "vue";

import PayRangePenetrationBar from "@components/PayRangePenetrationBar/PayRangePenetrationBar.vue";

import arrowUpIcon from "./icons/arrow-up.svg";
import warningIcon from "./icons/warning.svg";
import { formatFairPayDifferenceAmount, getCurrencySymbol } from "./formatFairPayStatusAmount";

const props = defineProps({
  status: {
    type: String,
    default: null,
    validator(value) {
      return value === null || [
        "below-min",
        "below",
        "near-exact",
        "exact",
        "above",
        "above-max",
        "no-range",
      ].includes(value);
    },
  },
  amount: {
    type: [Number, String],
    default: null,
  },
  currencyCode: {
    type: String,
    default: "USD",
  },
  /** Rule A-40: false renders the range penetration instead of the status chip. */
  readinessEnabled: {
    type: Boolean,
    default: true,
  },
  rangePenetration: {
    type: Object,
    default: null,
  },
});

const currencySymbol = computed(() => getCurrencySymbol(props.currencyCode));

const showBelowIcon = computed(() => props.status === "below");
const showAboveIcon = computed(() => props.status === "above");

const label = computed(() => {
  switch (props.status) {
    case "below-min":
      return "Below min";
    case "above-max":
      return "Above max";
    case "no-range":
      // Rule A-21 (ER-11959): the current version carries no pay range evidence,
      // so there is nothing to compare the amount against.
      return "Not Available";
    case "near-exact":
      return "Fair in Tolerance";
    case "exact":
      return "Exactly Fair";
    case "below": {
      const formatted = formatFairPayDifferenceAmount(props.amount, props.currencyCode);
      return formatted ? `${formatted} below` : "Below";
    }
    case "above": {
      const formatted = formatFairPayDifferenceAmount(props.amount, props.currencyCode);
      return formatted ? `${formatted} above` : "Above";
    }
    default:
      return "";
  }
});
</script>

<style scoped>
.fair-pay-status-short {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 11px;
  border: 1px solid;
  border-radius: 999px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
  box-sizing: border-box;
}

.fair-pay-status-short__label {
  flex-shrink: 0;
}

.fair-pay-status-short__icon {
  display: block;
  flex-shrink: 0;
}

.fair-pay-status-short__icon--below {
  width: 6px;
  height: 8px;
}

.fair-pay-status-short__icon--above {
  width: 13px;
  height: 11.6px;
}

.fair-pay-status-short--below-min,
.fair-pay-status-short--above-max {
  background: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

.fair-pay-status-short--below,
.fair-pay-status-short--above {
  background: #fffaeb;
  border-color: #fedf89;
  color: #b54708;
}

.fair-pay-status-short--near-exact,
.fair-pay-status-short--exact {
  background: #ecfdf3;
  border-color: #abefc6;
  color: #067647;
}

.fair-pay-status-short--no-range {
  background: #f2f4f7;
  border-color: #eaecf0;
  color: #344054;
}
</style>
